/**
 * Modal — reusable native modal primitive replacing the legacy
 * `the legacy modal` wrapper. Used by callers as a drop-in: the prop
 * surface (`isOpen`, `toggle`, `size`, `contentClassName`,
 * `containerClassName`, `disableOverlayDismiss`, `usePortal`, `style`)
 * intentionally mirrors `the legacy modal` so a swap is a literal import
 * change in the consumer.
 *
 *   - native `<div role="dialog" aria-modal>` (no the legacy component dependency)
 *   - portal-renders to `document.body` when `usePortal !== false` so
 *     z-index never fights an ancestor stacking context
 *   - scrim with click-to-dismiss (unless `disableOverlayDismiss`)
 *   - ESC closes
 *   - body scroll lock while open (cleanly restored on unmount)
 *   - z-index increments per open modal so nested modals stack correctly
 *   - `size` maps to a max-width preset, same scale the legacy modal used
 *     so existing `sm2 = 640px` etc. consumers stay visually identical
 *
 * Styling: callers pass `contentClassName` (e.g. `.load-fuel-modal`) and
 * own all visual treatment via that scope. This primitive ships only
 * the structural shell (scrim, positioning, sizing, z-index).
 *
 * Companion SCSS: `widgets/Modal.scss`.
 */

import React, {ReactNode, useCallback, useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import classNames from "classnames";
import "./Modal.scss";

export type ModalSize = "xs" | "xs1" | "xs2" | "sm" | "sm2" | "md" | "lg" | "xl" | "max";

interface ModalProps {
    isOpen: boolean;
    children: ReactNode;
    toggle?: () => void;
    size?: ModalSize;
    /** Class applied to the inner content card — owns the visual look. */
    contentClassName?: string;
    /** Class applied to the outer fixed-position wrapper (scrim layer). */
    containerClassName?: string;
    /** When true, clicking the scrim does NOT close the modal. */
    disableOverlayDismiss?: boolean;
    /** Render through a portal to document.body. Defaults to true. */
    usePortal?: boolean;
    /** Inline style on the card (CSS custom properties, max-width override, etc.). */
    style?: React.CSSProperties;
    /** Optional aria-labelledby reference. */
    ariaLabelledBy?: string;
    /** Optional aria-label fallback when no labelledby. */
    ariaLabel?: string;
}

/*
 * Module-scoped z-index counter — increments as modals open so nested
 * modals stack on top of their parent without per-call configuration.
 * Mirrors the `initialModalzIndex + offset` pattern the legacy modal used.
 */
const INITIAL_Z = 1000;
let openCount = 0;
let scrollY = 0;

function lockBodyScroll(): void {
    if (openCount === 0 && typeof document !== "undefined") {
        scrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = "0";
        document.body.style.right = "0";
    }
    openCount++;
}

function unlockBodyScroll(): void {
    openCount = Math.max(0, openCount - 1);
    if (openCount === 0 && typeof document !== "undefined") {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        window.scrollTo(0, scrollY);
    }
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    children,
    toggle,
    size = "sm",
    contentClassName,
    containerClassName,
    disableOverlayDismiss,
    usePortal = true,
    style,
    ariaLabelledBy,
    ariaLabel,
}) => {
    /*
     * Auto-incrementing z-index so nested modals (e.g. CardPaymentAddModal
     * spawned from inside LoadFuelModal) stack above their parent. The
     * +N offset is based on a snapshot of openCount taken when this modal
     * mounted; subsequent open modals get higher offsets.
     */
    /*
     * z-index is assigned when the modal OPENS (not at mount) off the count of
     * modals already open, so a modal opened on top of another always stacks
     * above it. The common pattern in this codebase mounts modals unconditionally
     * and toggles `isOpen`, so a mount-time value would leave both at the base
     * z-index; deferring to open time is what makes nested stacking actually hold.
     */
    const [zIndex, setZIndex] = useState(INITIAL_Z);
    const wasOpenRef = useRef(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const labelId = useId();
    const effectiveLabelledBy = ariaLabelledBy ?? (ariaLabel ? labelId : undefined);

    /* Track open/close transitions to lock/unlock scroll cleanly. */
    useEffect(() => {
        if (isOpen && !wasOpenRef.current) {
            wasOpenRef.current = true;
            setZIndex(INITIAL_Z + openCount * 10);
            lockBodyScroll();
        } else if (!isOpen && wasOpenRef.current) {
            wasOpenRef.current = false;
            unlockBodyScroll();
        }
        return () => {
            if (wasOpenRef.current) {
                wasOpenRef.current = false;
                unlockBodyScroll();
            }
        };
    }, [isOpen]);

    /* ESC closes when open + not explicitly disabled. */
    useEffect(() => {
        if (!isOpen || disableOverlayDismiss) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && toggle) toggle();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, disableOverlayDismiss, toggle]);

    const handleScrimClick = useCallback((e: React.MouseEvent) => {
        if (disableOverlayDismiss) return;
        if (e.target === e.currentTarget && toggle) toggle();
    }, [disableOverlayDismiss, toggle]);

    /*
     * Focus management for the `aria-modal` dialog: move focus into the dialog
     * on open, trap Tab/Shift+Tab within it (so keyboard users can't escape
     * into the obscured background), and restore focus to the trigger on close.
     */
    useEffect(() => {
        if (!isOpen) return;
        const node = contentRef.current;
        if (!node) return;
        const prevFocus = document.activeElement as HTMLElement | null;
        const focusable = (): HTMLElement[] =>
            Array.from(node.querySelectorAll<HTMLElement>("a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex=\"-1\"])")).filter((el) => el.offsetParent !== null);
        (focusable()[0] ?? node).focus();
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== "Tab") return;
            const f = focusable();
            if (f.length === 0) {
                e.preventDefault();
                node.focus();
                return;
            }
            const first = f[0];
            const last = f[f.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        node.addEventListener("keydown", onKey);
        return () => {
            node.removeEventListener("keydown", onKey);
            prevFocus?.focus?.();
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const tree =
        <div
            className={classNames("dp-modal", containerClassName)}
            onClick={handleScrimClick}
            style={{zIndex}}
        >
            <div
                ref={contentRef}
                tabIndex={-1}
                className={classNames(
                    "dp-modal__content",
                    `dp-modal__content--size-${size}`,
                    contentClassName,
                )}
                role="dialog"
                aria-modal="true"
                aria-labelledby={effectiveLabelledBy}
                style={style}
            >
                {ariaLabel && !ariaLabelledBy &&
                    <span id={labelId} className="dp-modal__sr-label">{ariaLabel}</span>
                }
                {children}
            </div>
        </div>;
    if (usePortal && typeof document !== "undefined") {
        return createPortal(tree, document.body);
    }
    return tree;
};

export default Modal;
